import _ from 'lodash';
import { colors, responsiveFontSizes } from '@mui/material';
import { createTheme as createTheme$1, alpha } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import { THEMES } from '../global/constants/index.js';
import typography from './typography.js';
import { strongShadows, softShadows } from './shadows.js';

const baseConfig = {
    direction: 'ltr',
    typography,
    overrides: {
        MuiLinearProgress: {
            root: {
                borderRadius: 3,
                overflow: 'hidden',
            },
        },
        MuiListItemIcon: {
            root: {
                minWidth: 32,
            },
        },
        MuiChip: {
            root: {
                backgroundColor: 'rgba(0,0,0,0.075)',
            },
        },
    },
};
const themeConfigs = [
    {
        name: THEMES.ESCURO,
        palette: {
            mode: 'dark',
            action: {
                active: 'rgba(255, 255, 255, 0.54)',
                hover: 'rgba(255, 255, 255, 0.04)',
                selected: 'rgba(255, 255, 255, 0.08)',
                disabled: 'rgba(255, 255, 255, 0.26)',
                disabledBackground: 'rgba(255, 255, 255, 0.12)',
                focus: 'rgba(255, 255, 255, 0.12)',
            },
            background: {
                default: '#282C34',
                dark: '#1c2025',
                paper: '#282C34',
            },
            primary: {
                main: '#3B96C2',
            },
            secondary: {
                main: '#3B96C2',
            },
            text: {
                primary: '#e6e5e8',
                secondary: '#adb0bb',
            },
            grey: {
                main: grey[300],
                dark: grey[400],
            },
        },
        shadows: strongShadows,
    },
    {
        name: THEMES.CIGAM,
        overrides: {
            MuiInputBase: {
                input: {
                    '&::placeholder': {
                        opacity: 1,
                        color: colors.blueGrey[600],
                    },
                },
            },
        },
        palette: {
            mode: 'light',
            action: {
                active: colors.blueGrey[600],
            },
            background: {
                default: colors.common.white,
                dark: '#f4f6f8',
                paper: colors.common.white,
            },
            primary: {
                main: '#307DA2',
            },
            secondary: {
                main: '#ff5811',
            },
            text: {
                primary: colors.blueGrey[900],
                secondary: colors.blueGrey[600],
            },
            grey: {
                main: grey[300],
                dark: grey[400],
            },
        },
        shadows: softShadows,
    },
];
const variantsComponents = (theme) => ({
    components: {
        MuiButton: {
            variants: [
                {
                    props: { variant: 'contained', color: 'grey' },
                    style: {
                        color: theme.palette.getContrastText(theme.palette.grey[300]),
                    },
                },
                {
                    props: { variant: 'outlined', color: 'grey' },
                    style: {
                        color: theme.palette.text.primary,
                        borderColor: theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.23)'
                            : 'rgba(255, 255, 255, 0.23)',
                        '&.Mui-disabled': {
                            border: `1px solid ${theme.palette.action.disabledBackground}`,
                        },
                        '&:hover': {
                            borderColor: theme.palette.mode === 'light'
                                ? 'rgba(0, 0, 0, 0.23)'
                                : 'rgba(255, 255, 255, 0.23)',
                            backgroundColor: alpha(theme.palette.text.primary, theme.palette.action.hoverOpacity),
                        },
                    },
                },
                {
                    props: { color: 'grey', variant: 'text' },
                    style: {
                        color: theme.palette.text.primary,
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.text.primary, theme.palette.action.hoverOpacity),
                        },
                    },
                },
            ],
        },
    },
});
function createTheme(settings) {
    const themeList = settings.themes || themeConfigs;
    let themeConfig = themeList.find(theme => theme.name === settings.theme);
    if (!themeConfig) {
        console.warn(new Error(`The theme ${settings.theme} is not valid`));
        [themeConfig] = themeConfigs;
    }
    let theme = createTheme$1(_.merge({}, baseConfig, themeConfig, { direction: settings.direction }));
    if (settings.responsiveFontSizes) {
        theme = responsiveFontSizes(theme);
    }
    theme = createTheme$1(theme, variantsComponents(theme));
    return theme;
}

export { createTheme };
